/** Short calendar date for profile / identity rows (es-UY). */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("es-UY", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = Date.now();
  const diff = (now - d.getTime()) / 1000;
  if (diff < 60) return "hace un momento";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  if (diff < 172800) return "ayer";
  return d.toLocaleDateString("es-UY", { day: "numeric", month: "short" });
}

/**
 * Timestamp for a chat bubble (es-UY). Same-day messages show only the time
 * ("14:32"); older messages prepend a short date ("11 jun, 14:32") so the
 * operator can tell an old transcript apart at a glance.
 */
export function formatMessageTimestamp(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const time = d.toLocaleTimeString("es-UY", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return time;
  const date = d.toLocaleDateString("es-UY", { day: "numeric", month: "short" });
  return `${date}, ${time}`;
}

export function statusLabel(status: string): string {
  const m: Record<string, string> = {
    active: "Activa",
    escalated: "Escalada",
    closed: "Cerrada",
    resolved: "Resuelta",
  };
  return m[status] ?? status;
}

/**
 * Short duration from conversation start to last persisted message.
 * Empty threads (no messages / no ``last_message_at``) return "—" — never
 * "now", or the value would grow on every render.
 */
export function formatConversationDuration(
  createdAt: string,
  lastMessageAt: string | null | undefined,
  messageCount = 0
): string {
  if (messageCount <= 0 || !lastMessageAt) return "—";
  const start = new Date(createdAt).getTime();
  const end = new Date(lastMessageAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return "—";
  const mins = Math.floor((end - start) / 60_000);
  if (mins < 1) return "<1m";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hours}h ${rem}m` : `${hours}h`;
}

export function formatResolutionRate(pct: number | null | undefined): string {
  if (pct == null) return "—";
  return `${pct}%`;
}

